import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, Legend, CartesianGrid,
} from "recharts";
import { TrendingUp, Building2, Target, PoundSterling, Activity, Zap } from "lucide-react";

const BAND_COLORS = {
  A: "#008054", B: "#19b459", C: "#8dce46",
  D: "#ffd500", E: "#fcaa1c", F: "#ef8023", G: "#e9153b",
  UNKNOWN: "#9ca3af",
};

const GBP = (n) => (n == null ? "—" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`);

export default function AnalyticsPage() {
  const [readiness, setReadiness] = useState(null);
  const [stats, setStats] = useState(null);
  const [sap, setSap] = useState(null);
  const [measures, setMeasures] = useState(null);
  const [traj, setTraj] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r, s, sp, m, t] = await Promise.all([
          api.get("/analytics/programme-readiness"),
          api.get("/stats"),
          api.get("/analytics/sap-distribution"),
          api.get("/analytics/measures-impact"),
          api.get("/analytics/trajectory"),
        ]);
        setReadiness(r.data);
        setStats(s.data);
        setSap(sp.data);
        setMeasures(m.data);
        setTraj(t.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="p-8 space-y-6" data-testid="analytics-loading"><Skeleton className="h-32" /><Skeleton className="h-80" /></div>;
  }

  const bandDist = (stats?.band_distribution || []).map((b) => ({
    band: b.band, count: b.count, fill: BAND_COLORS[b.band] || BAND_COLORS.UNKNOWN,
  }));
  const sapData = (sap?.buckets || []).map((b) => ({
    band: b.band, count: b.count, fill: BAND_COLORS[b.band] || BAND_COLORS.UNKNOWN,
  }));
  const measureData = (measures?.items || []).slice(0, 10).map((m) => ({
    name: m.label.length > 28 ? m.label.slice(0, 27) + "…" : m.label,
    fullName: m.label,
    count: m.count,
  }));
  const trajData = (traj?.points || []).map((p) => ({
    month: p.month, upgrade: p.upgrade, downgrade: p.downgrade, new_certificate: p.new_certificate,
  }));

  return (
    <div className="p-8 space-y-8" data-testid="analytics-page">
      {/* Hero */}
      <div>
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--hi-orange)" }}>PORTFOLIO ANALYTICS</div>
        <h1 className="font-heading font-black text-4xl mt-2 tracking-tight" style={{ color: "var(--hi-olive)" }}>
          The state of your stock<br />
          <span style={{ color: "var(--hi-orange)" }}>at a glance.</span>
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          Live picture of EPC bands, SAP distribution, retrofit-measure coverage and band-change trajectory across {stats?.total_properties?.toLocaleString("en-GB") ?? "—"} active properties.
        </p>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <HeadlineTile
          label="At band C or above"
          value={`${readiness?.pct_at_c ?? 0}%`}
          sub={`${(readiness?.at_or_above_c ?? 0).toLocaleString("en-GB")} of ${(readiness?.total_active ?? 0).toLocaleString("en-GB")}`}
          icon={<Target className="h-5 w-5" />}
          tone="success"
          testid="tile-at-c"
        />
        <HeadlineTile
          label="Below band C"
          value={(readiness?.below_c ?? 0).toLocaleString("en-GB")}
          sub="Needs retrofit programme"
          icon={<Building2 className="h-5 w-5" />}
          tone="warning"
          testid="tile-below-c"
        />
        <HeadlineTile
          label="Indicative £ to lift to C"
          value={GBP(readiness?.indicative_total_cost_to_c)}
          sub={`~${GBP(readiness?.avg_cost_per_property)} avg / property`}
          icon={<PoundSterling className="h-5 w-5" />}
          tone="primary"
          testid="tile-cost"
        />
        <HeadlineTile
          label="Band changes (30d)"
          value={(stats?.recent_changes_30d ?? 0).toLocaleString("en-GB")}
          sub="Tracked on the gov register"
          icon={<TrendingUp className="h-5 w-5" />}
          testid="tile-changes"
        />
      </div>

      {/* Band distribution donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-none border-border p-6 lg:col-span-1" data-testid="chart-band-donut">
          <SectionTitle icon={<Zap className="h-3.5 w-3.5" />}>Current band distribution</SectionTitle>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={bandDist} dataKey="count" nameKey="band" cx="50%" cy="50%"
                     innerRadius={55} outerRadius={100} stroke="white" strokeWidth={2}
                     paddingAngle={2}>
                  {bandDist.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v.toLocaleString("en-GB")} properties`, `Band ${n}`]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-4 gap-1.5 mt-3">
            {bandDist.map((b) => (
              <div key={b.band} className="text-center">
                <div className="h-1.5 mb-1" style={{ backgroundColor: b.fill }} />
                <div className="text-[10px] font-bold uppercase">{b.band}</div>
                <div className="text-xs font-mono text-muted-foreground">{b.count.toLocaleString("en-GB")}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* SAP distribution */}
        <Card className="rounded-none border-border p-6 lg:col-span-2" data-testid="chart-sap-distribution">
          <SectionTitle icon={<Activity className="h-3.5 w-3.5" />}>SAP distribution by band threshold</SectionTitle>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sapData} margin={{ top: 16, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6dfcc" />
                <XAxis dataKey="band" tick={{ fontSize: 12, fill: "#1f2a14" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6b6b58" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`${v.toLocaleString("en-GB")} properties`, "Count"]} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {sapData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
            SAP scores bucketed at each band threshold (G ≤ 20, F 21-38, E 39-54, D 55-68, C 69-80, B 81-91, A 92+).
          </div>
        </Card>
      </div>

      {/* Top measures + trajectory */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-none border-border p-6" data-testid="chart-top-measures">
          <SectionTitle icon={<Zap className="h-3.5 w-3.5" />}>Most-recommended retrofit measures</SectionTitle>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={measureData} layout="vertical" margin={{ top: 8, right: 20, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6dfcc" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#6b6b58" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#1f2a14" }} width={150} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(224,123,0,0.08)" }}
                  formatter={(v) => [`${v.toLocaleString("en-GB")} properties`, "Recommended at"]}
                  labelFormatter={(_, payload) => (payload && payload[0]?.payload?.fullName) || ""}
                  contentStyle={{ fontSize: 11, borderRadius: 0, border: "1px solid var(--hi-olive)" }}
                />
                <Bar dataKey="count" fill="var(--hi-orange)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-none border-border p-6" data-testid="chart-trajectory">
          <SectionTitle icon={<TrendingUp className="h-3.5 w-3.5" />}>Band change trajectory · last 12 months</SectionTitle>
          {trajData.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-sm text-muted-foreground">
              No band changes recorded in the last 12 months yet — run a full gov-register sync to populate this view.
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trajData} margin={{ top: 16, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e6dfcc" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b6b58" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b6b58" }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="upgrade" stroke="#19b459" strokeWidth={3} dot={{ r: 4 }} name="Upgrades" />
                  <Line type="monotone" dataKey="downgrade" stroke="#e9153b" strokeWidth={3} dot={{ r: 4 }} name="Downgrades" />
                  <Line type="monotone" dataKey="new_certificate" stroke="var(--hi-orange)" strokeWidth={2} dot={{ r: 3 }} name="New certs" strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function SectionTitle({ icon, children }) {
  return (
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-4">
      {icon}<span style={{ color: "var(--hi-olive)" }}>{children}</span>
    </div>
  );
}

function HeadlineTile({ label, value, sub, icon, tone, testid }) {
  const styles = {
    success: { bg: "#f0fdf4", border: "#16a34a", color: "#14532d" },
    warning: { bg: "#fff8ed", border: "var(--hi-orange)", color: "var(--hi-orange-deep)" },
    primary: { bg: "white", border: "var(--hi-olive)", color: "var(--hi-olive)" },
  }[tone] || { bg: "white", border: "#e6dfcc", color: "#1f2a14" };
  return (
    <div className="border-2 p-5 flex items-start justify-between"
         style={{ backgroundColor: styles.bg, borderColor: styles.border }}
         data-testid={testid}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: styles.color }}>{label}</div>
        <div className="font-heading font-black text-3xl mt-2 tracking-tight" style={{ color: styles.color }}>{value}</div>
        {sub && <div className="text-[11px] mt-1 leading-tight" style={{ color: styles.color, opacity: 0.7 }}>{sub}</div>}
      </div>
      <div style={{ color: styles.color, opacity: 0.6 }}>{icon}</div>
    </div>
  );
}
